<template>
  <div class="realEstate-page re-premium" style="min-height: 80vh">
    <div v-if="loaded === true">

      <!-- Premium hero -->
      <div class="re-hero">
        <div class="re-hero__glow"/>
        <div class="re-hero__inner">
          <div class="re-hero__avatar">
            <feather-icon icon="HomeIcon" size="26"/>
          </div>
          <div class="re-hero__meta">
            <h3 class="re-hero__title">{{ realEstate.name || msg('Unnamed property') }}</h3>
            <div class="re-hero__sub">
              <feather-icon icon="MapPinIcon" size="14"/>
              <span>{{ heroAddress }}</span>
              <span v-if="realEstate.id" class="re-hero__chip">ID {{ realEstate.id }}</span>
              <span class="re-hero__chip">{{ heroTypeText }}</span>
            </div>
          </div>
          <div class="re-hero__stats">
            <div v-if="heroRooms != null" class="re-hero__stat">
              <feather-icon icon="GridIcon" size="15"/><b>{{ heroRooms }}</b> {{ msg('Rooms') }}
            </div>
            <div v-if="heroPeople != null" class="re-hero__stat">
              <feather-icon icon="UsersIcon" size="15"/><b>{{ heroPeople }}</b> {{ msg('People') }}
            </div>
            <div v-if="heroPrice != null" class="re-hero__stat">
              <feather-icon icon="TagIcon" size="15"/><b>{{ heroPrice }}</b>
            </div>
          </div>
        </div>
      </div>

      <!-- Sticky action bar -->
      <div class="re-actionbar">
        <feather-icon :icon="realEstateUpdated ? 'AlertCircleIcon' : 'CheckCircleIcon'" size="18"
                      :class="realEstateUpdated ? 'text-warning' : 'text-success'"/>
        <div>
          <div class="re-actionbar__title">
            {{ realEstateUpdated ? msg('Unsaved changes') : msg('All changes saved') }}
          </div>
          <div class="re-actionbar__sub">{{ msg('Edit property details across the tabs below') }}</div>
        </div>
        <div class="re-actionbar__spacer"/>
        <b-button class="re-btn-cancel" @click="loadRealEstate">
          <feather-icon icon="RotateCcwIcon" size="15" class="mr-25"/>{{ msg("Cancel") }}
        </b-button>
        <b-button class="re-btn-save" @click="updateRealEstate">
          <feather-icon icon="SaveIcon" size="15" class="mr-25"/>{{ msg("Save") }}
        </b-button>
      </div>

      <b-tabs class="re-tabs client-panel-tabs tabs-title" nav-class="d-inline-flex" v-model="tabIndex">
        <!--Pictures, Managemnt details, Geografic details,Areas details -->
        <b-tab v-if="requestId!=null">
          <template #title>
            <feather-icon icon="MailIcon" size="15"/>
            {{ msg('Email Administration') }}
          </template>
          <real-estate-admin v-model="realEstate" @change="()=>realEstateUpdated=true" :request-id="requestId"/>
        </b-tab>
        <b-tab>
          <template #title>
            <feather-icon icon="SettingsIcon" size="15"/>
            {{ msg('Management') }}
          </template>

          <!-- Management details (child owns its section cards) -->
          <real-estate-management v-model="realEstate" @change="()=>realEstateUpdated=true"
                                  :building="building"/>

          <!--Pictures-->
          <div class="re-section re-section--info">
            <div class="re-section__head">
              <div class="re-section__icon"><feather-icon icon="ImageIcon" size="20"/></div>
              <div class="re-section__titles">
                <div class="re-section__title">{{ msg('Pictures') }}</div>
                <div class="re-section__sub">{{ msg('Property images, title photo and floor plans') }}</div>
              </div>
              <div class="re-section__head-actions">
                <b-button variant="outline-primary" class="btn-icon" id="realEstate-upload-image">
                  <feather-icon icon="UploadIcon"/>
                </b-button>
                <b-popover target="realEstate-upload-image" size="xl">
                  <vue-dropzone id="video-dropzone"
                                :options="dropzoneOptions"
                                :useCustomSlot=true
                                :duplicateCheck="true"
                                @vdropzone-complete="uploadComplete"
                                @vdropzone-success="uploadSuccess">
                    <div class="dropzone-custom-content">
                      <h3 class="dropzone-custom-title">Drag and drop to upload content!</h3>
                      <div class="subtitle">...or click to select a file from your computer</div>
                    </div>
                  </vue-dropzone>
                </b-popover>
              </div>
            </div>
            <div class="re-section__body">
              <div class="re-callout re-callout--warning mb-2">
                <feather-icon class="re-callout__icon" icon="InfoIcon" size="16"/>
                <span>*Grundrisse die hochgeladen werden , müssen das Wort "Grundriss" im Bildnamen enthalten.</span>
              </div>
              <realEstate-image-swiper v-model="realEstate.attachments" @change="realEstateUpdated=true"/>
            </div>
          </div>

          <!-- Geographic details (child owns its section card) -->
          <real-estate-geo v-model="realEstate" @change="()=>realEstateUpdated=true"/>

          <!-- Areas details (child owns its section card) -->
          <real-estate-area v-model="realEstate" @change="()=>realEstateUpdated=true"/>

          <!--files-->
          <div class="re-section re-section--warning">
            <div class="re-section__head">
              <div class="re-section__icon"><feather-icon icon="FolderIcon" size="20"/></div>
              <div class="re-section__titles">
                <div class="re-section__title">{{ msg('Files') }}</div>
                <div class="re-section__sub">{{ msg('Documents shared with clients and owners') }}</div>
              </div>
              <div class="re-section__head-actions">
                <b-button size="sm" v-bind:variant="uploadFile ? 'warning' : 'outline-warning'"
                          @click="()=>uploadFile =!uploadFile">
                  <feather-icon icon="UploadIcon" size="14" class="mr-25"/>{{ msg("Upload file") }}
                </b-button>
              </div>
            </div>
            <div class="re-section__body">
              <div>
                <div class="files-data">
                  <div class="upload-backdrop" v-show="uploadFile === true">
                    <vue-dropzone id="contract-upload" class="h-100" :options="dropzoneOptionsFiles" :useCustomSlot=true
                                  :duplicateCheck="true" @vdropzone-complete="uploadCompleteFile"
                                  @vdropzone-success="uploadSuccessFile">
                      <div class="dropzone-custom-content">
                        <h3 class="dropzone-custom-title">Drag and drop to upload content!</h3>
                        <div class="subtitle">...or click to select a file from your computer</div>
                      </div>
                    </vue-dropzone>
                  </div>
                  <b-table-simple outlined>
                    <b-tr>
                      <b-th class="text-center" style="width: 20%">{{ msg('File') }}</b-th>
                      <b-th class="text-center" style="width: 20%">{{ msg('Tag') }}</b-th>
                      <!--
                                            <b-th class="text-center" style="width: 20%">{{ msg('Client note') }}</b-th>
                      -->
                      <b-th class="text-center" style="width: 20%">{{ msg('Note') }}</b-th>
                      <b-th class="text-center" style="width: 10%">{{ msg("Visible") }}</b-th>
                      <b-th class="text-center" style="width: 15%">{{ msg('Operations') }}</b-th>
                    </b-tr>
                  </b-table-simple>
                  <perfect-scrollbar class="position-relative " style="height: 250px">
                    <b-table-simple outlined>
                      <b-tr v-for="(file,index) in (realEstate.files || [])" v-bind:key="file.id"
                            style="white-space: nowrap;text-overflow: ellipsis" v-show="file.active">
                        <b-td style="width: 20%">
                          <i v-bind:class="iconByExt(file.filename)" class="mx-1"
                             style="font-size: 20px"></i>{{ file.filename }}
                        </b-td>
                        <b-td class="text-center" style="width: 20%">
                          <v-select :options="fileTags" :reduce="(tag) => tag.value" label="title" v-model="file.tag"
                                    append-to-body @input="()=>submitDocumentFile(index)">
                            <template v-slot:option="option">
                              <i class="d-inline-block text-left" style="width: 30px" :class="option.icon"></i>
                              <span>{{ option.title }}</span>
                            </template>
                          </v-select>
                        </b-td>
                        <!--                        <b-td class="text-center" style="width: 20%">
                                                  <b-form-textarea v-model="file.clientNote" @change="()=>submitDocumentFile(index)"
                                                                   size="sm"></b-form-textarea>
                                                </b-td>-->
                        <b-td class="text-center" style="width: 20%">
                          <b-form-textarea v-model="file.notes" @change="()=>submitDocumentFile(index)"
                                           size="sm"></b-form-textarea>
                        </b-td>
                        <b-td class="text-center" style="width: 10%">
                          <b-form-checkbox v-model="file.visible" class="mx-1" name="check-button" size="sm"
                                           @change="()=>submitDocumentFile(index)"
                                           v-b-tooltip="msg('visible to client')"
                                           button
                                           v-bind:button-variant="file.visible ? 'outline-success' : 'outline-warning'">
                            <i class="fas fa-user"></i>
                          </b-form-checkbox>
                          <b-form-checkbox v-model="file.visibleForOwner" name="check-button" size="sm"
                                           @change="()=>submitDocumentFile(index)"
                                           v-b-tooltip="msg('visible to owner')"
                                           button
                                           v-bind:button-variant="file.visibleForOwner ? 'outline-success' : 'outline-warning'">
                            <i class="fas fa-user-tie"></i>
                          </b-form-checkbox>
                        </b-td>
                        <b-td class="text-center" style="width: 15%">
                          <b-link class="text-secondary mx-1"
                                  @click="()=>onHideFile(index)"><i
                              class="fas fa-eye-slash"></i></b-link>
                          <b-link><i class="icon fas fa-download  w-auto" @click="()=>onDownloadFile(file.id)"></i>
                          </b-link>
                        </b-td>
                      </b-tr>
                    </b-table-simple>
                  </perfect-scrollbar>
                </div>
              </div>
            </div>
          </div>
        </b-tab>
        <!-- RealEstate Description -->
<!--        <b-tab :title="msg('Description')">
          <b-col cols="12" class="col-12">
            <b-card no-body class="card-stats ">
              <b-card-body>
                <div>
                  &lt;!&ndash; Header: Personal Info &ndash;&gt;
                  <div class="d-flex">
                    <feather-icon icon="PackageIcon" size="19"/>
                    <h4 class="mb-0 ml-50">
                      {{ msg('Description') }}
                    </h4>
                  </div>
                  <real-estate-text v-model="realEstate" @change="()=>realEstateUpdated=true"/>
                </div>
              </b-card-body>
            </b-card>
          </b-col>
        </b-tab>-->
        <!--furniture details, Prices details  -->
        <b-tab>
          <template #title>
            <feather-icon icon="DollarSignIcon" size="15"/>
            {{ msg('Prices') }}
          </template>
          <real-estate-prices v-model="realEstate" @change="()=>realEstateUpdated=true"/>
        </b-tab>
        <b-tab>
          <template #title>
            <feather-icon icon="PackageIcon" size="15"/>
            {{ msg('Furniture') }}
          </template>
          <real-estate-furniture v-model="realEstate.realEstateFurnishing"
                                 @change="()=>realEstateUpdated=true"/>
          <real-estate-resources v-model="realEstate" @change="()=>realEstateUpdated=true"/>
        </b-tab>
        <!--Contact details  -->
        <b-tab>
          <template #title>
            <feather-icon icon="UserIcon" size="15"/>
            {{ msg('Contact') }}
          </template>
          <real-estate-contact v-model="realEstate" @change="()=>realEstateUpdated=true" @refresh="sendSubmitted"/>
        </b-tab>
        <!--Nuki details -->
        <b-tab>
          <template #title>
            <feather-icon icon="KeyIcon" size="15"/>
            {{ msg('Nuki') }}
          </template>
          <real-estate-nuki v-model="realEstate" @change="()=>realEstateUpdated=true"/>
        </b-tab>

        <b-tab v-if="realEstate.marketingType.rent">
          <template #title>
            <feather-icon icon="CalendarIcon" size="15"/>
            {{ msg('Treasure Rent') }}
          </template>
          <real-estate-treasure-rent v-model="realEstate" @change="()=>realEstateUpdated=true"/>
        </b-tab>
<!--
        <b-tab v-if="realEstate.marketingType.rent" :title="msg('Channels')">

          <b-card no-body class="card-stats ">
            <b-card-body>
              <div>
                &lt;!&ndash; Header: Personal Info &ndash;&gt;
                <div class="d-flex">
                  <feather-icon icon="PackageIcon" size="19"/>
                  <h4 class="mb-0 ml-50">
                    {{ msg('Channels').toUpperCase() }}
                  </h4>
                </div>
                <real-estate-channels v-model="realEstate" @change="()=>realEstateUpdated=true"/>
              </div>
            </b-card-body>
          </b-card>


        </b-tab>
-->


        <b-tab v-if="realEstate.marketingType.rent">
          <template #title>
            <feather-icon icon="CreditCardIcon" size="15"/>
            {{ msg('Expenses') }}
          </template>
          <real-estate-expenses v-model="realEstate" @loadRealEstate="loadRealEstate"/>
        </b-tab>

        <b-tab>
          <template #title>
            <feather-icon icon="CheckSquareIcon" size="15"/>
            {{ msg('Todo List') }}
          </template>
          <div class="re-section re-section--success">
            <div class="re-section__head">
              <div class="re-section__icon"><feather-icon icon="CheckSquareIcon" size="20"/></div>
              <div class="re-section__titles">
                <div class="re-section__title">{{ msg('Todo List') }}</div>
                <div class="re-section__sub">{{ msg('Tasks and to-dos for this property') }}</div>
              </div>
            </div>
            <div class="re-section__body">
              <real-estate-todo v-model="realEstate" @change="()=>realEstateUpdated=true"/>
            </div>
          </div>
        </b-tab>


<!--
        <b-tab :title="msg('Calendar')">


        </b-tab>
-->

        <!-- Damages Tab-->
        <b-tab v-if="realEstate">
          <template #title>
            <feather-icon icon="AlertTriangleIcon" size="15"/>
            {{ msg('Damages') }}
          </template>
          <div class="re-section re-section--danger">
            <div class="re-section__head">
              <div class="re-section__icon"><feather-icon icon="AlertTriangleIcon" size="20"/></div>
              <div class="re-section__titles">
                <div class="re-section__title">{{ msg('Damages') }}</div>
                <div class="re-section__sub">{{ msg('Reported damages for this property') }}</div>
              </div>
              <div class="re-section__head-actions">
                <b-link style="color: inherit" @click="$refs.damage.refresh()">
                  <feather-icon icon="RefreshCcwIcon" size="16"/>
                </b-link>
              </div>
            </div>
            <div class="re-section__body">
              <damage-panel ref="damage" :realEstatesIds="realEstate.id"/>
            </div>
          </div>
        </b-tab>

        <b-tab v-if="Array.isArray(realEstate.priceLabs)">
          <template #title>
            <feather-icon icon="TrendingUpIcon" size="15"/>
            PriceLabs
          </template>
          <real-estate-price-labs v-for="priceLab in realEstate.priceLabs" v-bind:price-labs="priceLab"
                                  v-bind:key="priceLab.pms"/>
        </b-tab>

      </b-tabs>

<!--      <template v-if="tabIndex === 10">
        <calendar-rent v-if="realEstate.bookings!=null&&realEstate.bookings.length>0" :property-id="realEstate.id"
                       is-month></calendar-rent>

      </template>-->
      <!--      <template
                v-if="realEstate.realEstateTreasure != null && realEstate.realEstateTreasure.review != null && thisPage">
              <script
                  type="application/javascript"
                  src="https://www.revyoos.com/js/widgetBuilder.js"
                  :data-revyoos-widget="realEstate.realEstateTreasure.review"
                  ref="revyoosScript"
              ></script>
            </template>-->

    </div>
    <div v-else>
      <div class="d-flex align-items-center" style="min-height: 600px">
        <div class="d-inline-block w-100 text-center">
          <b-spinner class="mx-1" variant="primary"/>
          <strong>{{ msg('Loading') }}...</strong>
        </div>
      </div>
    </div>
  </div>

</template>

<script>

import {mapActions, mapGetters, mapState} from 'vuex'
import vue2Dropzone from 'vue2-dropzone'
import RealEstateImageSwiper from '@/components/panels/realEstates/realEstateImageSwiper'
import RealEstateGeo from '@/components/panels/realEstates/realEstateGeo'
import RealEstateNuki from '@/components/panels/realEstates/realEstateNuki'
import RealEstateAdmin from "@/components/panels/realEstates/realEstateAdmin";
import RealEstateFurniture from '@/components/panels/realEstates/realEstateFurniture'
import RealEstatePrices from '@/components/panels/realEstates/realEstatePrices'
import RealEstateContact from '@/components/panels/realEstates/realEstateContactPerson'
import RealEstateArea from '@/components/panels/realEstates/realEstateAreas'
import RealEstateManagement from '@/components/panels/realEstates/realEstateManagement'
import RealEstateMetaEdit from '@/components/panels/realEstates/realEstateMetaEdit'
import RealEstateText from "@/components/panels/realEstates/realEstateTexts";
import {mergeDeep} from "@/plugins/custom-prototypes";
import RealEstateTreasureRent from "@/components/panels/realEstates/realEstateTreasureRent";
import CalendarRent from "@/views/calendar/CalendarRent";
import RealEstateExpenses from "@/components/panels/realEstates/realEstateExpenses";
import RealEstateTodo from "@/components/panels/realEstates/realEstateTodo";
import RealEstatePriceLabs from "@/components/panels/realEstates/RealEstatePriceLabs.vue";
import {PriceLabs} from "@/model/RealEstate/priceLabs/PriceLabsDetails";
import RealEstateChannels from "@/components/panels/realEstates/realEstateChannels.vue";
import DamagePanel from "@/components/panels/damages/DamagePanel.vue";
import RealEstateResources from "@/components/panels/realEstates/realEstateResources.vue";

const defaultRealEstate = {
  contactPerson: {},
  realEstateState: {},
  realEstateManagement: {},
  furnishes: {},
  realEstateTexts: {},
  realEstatePrices: {},
  realEstateTreasure: {},
  resources: {},
  attachments: [],
  priceLabs: []
};

export default {
  name: 'editRealEstate',
  components: {
    DamagePanel,
    RealEstatePriceLabs,
    // eslint-disable-next-line vue/no-unused-components
    RealEstateText,
    RealEstateResources,
    // eslint-disable-next-line vue/no-unused-components
    RealEstateMetaEdit,
    RealEstateGeo,
    RealEstateNuki,
    RealEstateAdmin,
    RealEstateArea,
    // eslint-disable-next-line vue/no-unused-components
    RealEstateFurniture,
    RealEstateContact,
    RealEstateExpenses,
    RealEstateTodo,
    RealEstatePrices,
    RealEstateManagement,
    RealEstateImageSwiper,
    vueDropzone: vue2Dropzone,
    RealEstateTreasureRent,
    // eslint-disable-next-line vue/no-unused-components
    RealEstateChannels,
    // eslint-disable-next-line vue/no-unused-components
    CalendarRent
  },
  data() {
    return {
      loaded: false,
      error: null,
      thisPage: false,

      realEstate: {name: '', furnishes: {}, resources: {}, realEstateCancel: {}},
      building: {},
      tabIndex: 0,
      saveButton: 0,
      requestId: null,
      realEstateUpdated: false,
      dropzoneOptions: {},

      uploadFile: false,

      images: [{
        id: 1,
        imgSrc: require('@/assets/images/icons/pdf.png')
      }
      ],
      //Listeners
      listeners: {
        submit: () => {
        }
      },
    }
  },
  mounted() {
    this.thisPage = true;

  },
  beforeDestroy() {
    /* this.thisPage = false;

     console.log("beforeDestroy")
     window.document.getElementById('scp_iframe_general').remove();*/
  },

  watch: {
    id: function () { // watch it
      this.onLoad()
      this.error = null
    },
  }
  ,
  computed: {
    ...mapGetters('translator', ['msg']),
    ...mapGetters('data', ['getAllLanguage', 'getAllBranches', 'getAllCountries', 'getAdminRoleId']),
    ...mapGetters('realEstate', ['fileTags']),
    heroAddress() {
      const r = this.realEstate || {};
      if (r.fullAddress) return r.fullAddress;
      const line1 = [r.address, r.house].filter(Boolean).join(' ');
      const line2 = [r.zip, r.city].filter(Boolean).join(' ');
      const full = [line1, line2].filter(Boolean).join(', ');
      return full || this.msg('No address');
    },
    heroTypeText() {
      const m = this.realEstate && this.realEstate.marketingType;
      if (m && m.rent) return this.msg('Rent');
      if (m && m.purchase) return this.msg('Purchase');
      return this.msg('Property');
    },
    heroRooms() {
      const a = this.realEstate && this.realEstate.realEstateArea;
      return a && a.rooms != null ? a.rooms : null;
    },
    heroPeople() {
      const r = this.realEstate || {};
      if (r.numberOfPeople != null) return r.numberOfPeople;
      const a = r.realEstateArea;
      if (a && a.peoples != null) return a.peoples;
      return null;
    },
    heroPrice() {
      const p = this.realEstate && this.realEstate.realEstatePrices;
      if (!p) return null;
      const val = p.rentTotal != null ? p.rentTotal : (p.basePrice != null ? p.basePrice : null);
      return val != null ? ('€' + val) : null;
    },
    dropzoneOptionsFiles() {
      if (this.realEstate == null || this.realEstate.id == null)
        return {};
      return {
        url: window.config.apiUrl + 'realEstate/upload/file/' + this.realEstate.id,
        headers: {'Authorization': `Bearer ${localStorage.Bearer}`},
        createImageThumbnails: false,
        thumbnailWidth: 200,
        maxFiles: 10, // Set to desired limit or remove for unlimited
        //uploadMultiple: true, // Allows multiple files in a single request if supported
        multiple: true, // Allows multiple files to be uploaded if supported
        parallelUploads: 10, // Number of files to upload at once
        thumbnailHeight: 200,
        addRemoveLinks: true,
        maxFilesize: 10000000000,
        acceptedFiles: "image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.tsv,.ppt,.pptx,.mp4,.mp3",
      }
    }
    ,
    canSubmit: {
      get() {
        return false
      }
      ,
      save(v) {
        this.$emit('update:canSave', v)
      }
    }
    ,
    allBranches() {
      return Object.values(this.getAllBranches)
    }
    ,
  }
  ,
  created() {
    if (this.$route.params.id == null) {
      return this.error = 'Invalid realEstate id'
    }
    this.id = this.$route.params.id;
    this.requestId = this.$route.params.requestId;
    Promise.all([this._loadRealEstateData()])
        .finally(() => this.onLoad())
  }
  ,

  methods: {
    ...
        mapActions('document', ['downloadCustomFile']),
    ...
        mapActions('realEstate', {
          _getRealEstate: 'getRealEstateById',
          _getRealEstateWithBuilding: 'getRealEstateByIdWithBuilding',
          _updateRealEstate: 'updateRealEstate',
          _updateDocumentFile: 'updateDocumentFile',
          _loadRealEstateData: 'getData'
        }),
    uploadCompleteFile(file, response) {
      let responseData = JSON.parse(file.xhr.responseText);
      let realEstate = responseData.objects["RealEstate"];
      this.realEstate = realEstate;
    }
    ,
    uploadSuccessFile(file, response) {
      try {
        if (response.data == null || response.data['RealEstate'] == null)
          return;
        this.realEstate = response.data['RealEstate'];
      } catch (e) {
        console.error('uploadSuccess error', e)
      }
    }
    ,

    onHideFile(index) {
      let file = this.realEstate.files[index];
      file.visible = false;
      file.visibleForOwner = false;
      file.active = false;
      this.submitDocumentFile(index)
    }
    ,
    submitDocumentFile(index) {
      this._updateDocumentFile({
        id: this.realEstate.id,
        file: this.realEstate.files[index]
      });
    }
    ,
    onDownloadFile(fileId) {
      let $this = this;
      this.downloadCustomFile({fileId: fileId}).then(file => {
        $this.asyncDownloadFile(file)
      })
    }
    ,
    iconByExt(file) {
      if (file == null) {
        return {fas: true, 'fa-file-alt': true};
      }
      let ext = file.substr(file.lastIndexOf('.') + 1)
      if (ext === 'pdf') {
        return {far: true, 'fa-file-pdf': true}
      }
      if (ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'gift' || ext === 'bmp') {
        return {far: true, ' fa-file-image': true}
      }
      if (ext === 'doc' || ext === 'docx') {
        return {far: true, ' fa-file-word': true}
      }
      if (ext === 'xls' || ext === 'xlsx') {
        return {far: true, ' fa-file-word': true}
      }
      if (ext === 'zip' || ext === 'rar' || ext === '7zip') {
        return {far: true, ' fa-file-archive': true}
      }
      return {fas: true, 'fa-file-alt': true}
    }
    ,
    onLoad() {
      this.loaded = false

      this.loadRealEstate()

    }
    ,
    onEditorChange({quill, html, text}) {
      //Little hack in order to prevent onValueUpdated be triggered
      if (this.realEstate.description === html)
        return;
      this.realEstate.description = html;
      this.onValueUpdated({quill, html, text})
    }
    ,
    loadDropZone() {
      this.dropzoneOptions = {
        url: window.config.apiUrl + 'realEstate/upload/img',
        headers: {
          'Authorization': `Bearer ${localStorage.Bearer}`,
          'realEstateId': this.realEstate.id
        },
        paramName: 'file', // The name expected by the backend for the file parameter
        createImageThumbnails: false,
        thumbnailWidth: 200,
        thumbnailHeight: 200,
        addRemoveLinks: true,
        maxFilesize: 100, // Adjust to suitable size in MB
        maxFiles: 10, // Adjust to desired file limit
        uploadMultiple: true, // Send multiple files in a single request
        multiple: true, // Allow multiple files to be uploaded
        parallelUploads: 10, // Number of files to process in one go
        acceptedFiles: "image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.tsv,.ppt,.pptx,.mp4,.mp3"
      };
    }

    ,
    updatedHours: function (val) {
      // do whatever you want here
      console.log('updatedHours', val)
    }
    ,
    loadRealEstate() {
      let $this = this
      $this.loaded = false
      this._getRealEstateWithBuilding($this.id)
          .then((ans) => {
            let realEstate = ans["RealEstate"];
            console.log('the realEstate is', realEstate)
            realEstate = mergeDeep(realEstate, defaultRealEstate);
            realEstate.priceLabs = (realEstate.priceLabs || []).map(p => PriceLabs(p))
            $this.realEstate = realEstate;
            // Building can come back null when the property was unlinked from
            // its building — fall back to an empty object so child components
            // don't NPE on `building.id`.
            $this.building = ans["Building"] || {};
            //Set page as loaded
            $this.loaded = true
            $this.realEstateUpdated = false;
            $this.checkNullObjects(realEstate);
            $this.loadDropZone();
          }, (error) => {
            $this.error = error
          })
    }
    ,
    checkNullObjects(realEstate) {
      if (realEstate.realEstateManagement == null) {
        realEstate.realEstateManagement = {}
      }
      if (realEstate.realEstateState == null) {
        realEstate.realEstateState = {}
      }
      if (realEstate.contactPerson == null) {
        realEstate.contactPerson = {}
      }
      if (realEstate.attachments == null) {
        realEstate.attachments = []
      }
      if (realEstate.realEstateFurnishing == null) {
        realEstate.realEstateFurnishing = {}
      }
      if (realEstate.realEstateTexts == null) {
        realEstate.realEstateTexts = {}
      }
      if (realEstate.realEstatePrices == null) {
        realEstate.realEstatePrices = {}
      }

      if (realEstate.realEstateCancel == null) {
        realEstate.realEstateCancel = {}
      }

      if (realEstate.realEstateCancel.threeDaysFeePercent == null) {
        realEstate.realEstateCancel.threeDaysFeePercent = 20;
      }
      if (realEstate.realEstateCancel.sevenDaysFeePercent == null) {
        realEstate.realEstateCancel.sevenDaysFeePercent = 15;
      }
      if (realEstate.realEstateCancel.fourteenDaysFeePercent == null) {
        realEstate.realEstateCancel.fourteenDaysFeePercent = 10;
      }

    }
    ,
    uploadComplete(file, response) {
      this.loadRealEstate()

      console.log('uploadComplete', {file, response})
    }
    ,
    uploadSuccess(file, response) {
      try {
        if (response.data == null || response.data['RealEstate'] == null)
          return;
        this.realEstate.images = response.data['RealEstate']['images'];
      } catch (e) {
        console.error('uploadSuccess error', e)
      }
    }
    ,
    updateRealEstate() {
      let $this = this
      this._updateRealEstate(this.realEstate)
          .then(
              (a) => $this.$emit('submitted', {
                status: true,
                server: a
              }),
              (a) => $this.$emit('submitted', {
                status: false,
                server: a
              })
          )
    },
    sendSubmitted() {
      window.location.reload();
    }

    ,
    validate(field) {
      return this.$v.realEstate[field] != null && this.$v.realEstate[field].$error === true
    }
    ,
    submit() {
      if (this.id !== 0) {
        return this.updateRealEstate()
      }
      return this.createRealEstate()

    }
    ,


  }
  ,

}
</script>

<style>
.realEstate-page .update-realEstate {
  position: fixed;
  bottom: 5%;
  z-index: 100;
  transition: all 0.5s ease;
  right: 79px;
  opacity: 0;
}

.realEstate-page .update-realEstate.active {
  opacity: 1;
}

.realEstate-page .update-realEstate .btn-primary {
  -webkit-box-shadow: 0 1px 20px 1px #7367f0 !important;
  box-shadow: 0 1px 20px 1px #7367f0 !important;
}

.realEstate-page .update-realEstate .btn-warning {
  -webkit-box-shadow: 0 1px 20px 1px #ff8510 !important;
  box-shadow: 0 1px 20px 1px #ff8510 !important;
}

.realEstate-page .dz-preview, .dz-file-preview {
  display: none;
}
</style>
